import { onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';

export const useInvalidateCache = (keys) => {
	if (typeof keys === 'string') keys = [keys];
};

/**
 * `useInvalidationObserver` is a composable that allows a component to
 * define a number of SWRV cache keys that should be invalidated by other
 * components taking state-changing actions.
 *
 * Items is an array of { key, mutate } objects, where key is either a string
 * or a function, and mutate is the function returned by useSWRV.
 */
export const useInvalidationObserver = (items) => {
	if (!(items instanceof Array)) items = [items];

	// items array with each reactive key resolved.
	const itemsResolved = items.map((obj) => {
		const key = typeof obj.key === 'function' ? obj.key() : obj.key;
		return {
			key,
			mutate: obj.mutate,
		};
	});

	// Gets only the keys that are reactive from the items argument (ie,
	// the key is a function.)
	const reactiveKeys = items
		.map(({ key }) => key)
		.filter((key) => typeof key === 'function');

	const $store = useStore();
	const keysInUse = new Set(itemsResolved.map(({ key }) => key));
	$store.dispatch('addMutates', itemsResolved);

	watch(reactiveKeys, (newKeys, prevKeys) => {
		const updates = [];
		for (let i = 0; i < newKeys.length; i++) {
			keysInUse.delete(prevKeys[i]);
			keysInUse.add(newKeys[i]);
			updates.push({ prevKey: prevKeys[i], key: newKeys[i] });
		}
		$store.dispatch('updateMutatesKeys', updates);
	});

	onUnmounted(() => {
		$store.dispatch('removeMutates', keysInUse.values());
	});
};
